import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  TrashIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Button,
  Table,
  Badge,
  Dropdown,
  ConfirmDialog,
  EmptyState,
  Modal,
  Input,
  Select,
  Textarea,
} from '../components/ui';
import { useCouponStore } from '../stores';
import type { Coupon, CreateCouponData, DiscountType } from '../types';
import { formatDate, formatCurrency } from '../utils/helpers';

const discountTypeOptions = [
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'fixed_amount', label: 'Fixed Amount ($)' },
];

interface CouponFormData {
  code: string;
  description: string;
  discountType: DiscountType;
  discountValue: string;
  expirationDate: string;
  usageLimit: string;
  isActive: boolean;
}

const initialFormData: CouponFormData = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: '',
  expirationDate: '',
  usageLimit: '',
  isActive: true,
};

export const Coupons: React.FC = () => {
  const {
    coupons,
    isLoading,
    fetchCoupons,
    createCoupon,
    updateCoupon,
    deleteCoupon,
  } = useCouponStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [formData, setFormData] = useState<CouponFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState<Coupon | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const handleOpenModal = (coupon?: Coupon) => {
    if (coupon) {
      setEditingCoupon(coupon);
      setFormData({
        code: coupon.code,
        description: coupon.description,
        discountType: coupon.discount.type,
        discountValue: String(coupon.discount.value),
        expirationDate: coupon.expirationDate.split('T')[0],
        usageLimit: coupon.usageLimit ? String(coupon.usageLimit) : '',
        isActive: coupon.isActive,
      });
    } else {
      setEditingCoupon(null);
      setFormData(initialFormData);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCoupon(null);
    setFormData(initialFormData);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const data: CreateCouponData = {
        code: formData.code.toUpperCase(),
        description: formData.description,
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        expirationDate: formData.expirationDate,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit, 10) : null,
        isActive: formData.isActive,
      };

      if (editingCoupon) {
        await updateCoupon(editingCoupon.id, data);
        toast.success('Coupon updated successfully');
      } else {
        await createCoupon(data);
        toast.success('Coupon created successfully');
      }
      handleCloseModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save coupon';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (coupon: Coupon) => {
    setCouponToDelete(coupon);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!couponToDelete) return;
    setIsDeleting(true);
    try {
      await deleteCoupon(couponToDelete.id);
      toast.success('Coupon deleted successfully');
      setDeleteDialogOpen(false);
      setCouponToDelete(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete coupon';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.discount.type === 'percentage') {
      return `${coupon.discount.value}%`;
    }
    return formatCurrency(coupon.discount.value);
  };

  const isExpired = (expirationDate: string) => {
    return new Date(expirationDate) < new Date();
  };

  const getStatusBadge = (coupon: Coupon) => {
    if (!coupon.isActive) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    if (isExpired(coupon.expirationDate)) {
      return <Badge variant="danger">Expired</Badge>;
    }
    if (coupon.usageLimit && coupon.usageCount >= coupon.usageLimit) {
      return <Badge variant="warning">Limit Reached</Badge>;
    }
    return <Badge variant="success" dot>Active</Badge>;
  };

  const columns = [
    {
      key: 'code',
      header: 'Code',
      render: (coupon: Coupon) => (
        <span className="font-mono font-medium text-secondary-900 dark:text-secondary-100">{coupon.code}</span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (coupon: Coupon) => (
        <span className="text-secondary-600 dark:text-secondary-400 max-w-xs truncate block">{coupon.description}</span>
      ),
    },
    {
      key: 'discount',
      header: 'Discount',
      render: (coupon: Coupon) => (
        <span className="font-medium text-secondary-900 dark:text-secondary-100">{formatDiscount(coupon)}</span>
      ),
    },
    {
      key: 'expiration',
      header: 'Expiration',
      render: (coupon: Coupon) => (
        <span className={isExpired(coupon.expirationDate) ? 'text-danger-600 dark:text-danger-400' : 'text-secondary-600 dark:text-secondary-400'}>
          {formatDate(coupon.expirationDate)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (coupon: Coupon) => getStatusBadge(coupon),
    },
    {
      key: 'usage',
      header: 'Usage',
      render: (coupon: Coupon) => (
        <span className="text-secondary-600 dark:text-secondary-400">
          {coupon.usageCount}{coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (coupon: Coupon) => (
        <Dropdown
          trigger={
            <button
              className="p-1 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <EllipsisVerticalIcon className="h-5 w-5 text-secondary-500 dark:text-secondary-400" />
            </button>
          }
          items={[
            {
              label: 'Edit',
              icon: <PencilIcon className="h-4 w-4" />,
              onClick: () => handleOpenModal(coupon),
            },
            {
              label: 'Delete',
              icon: <TrashIcon className="h-4 w-4" />,
              onClick: () => handleDeleteClick(coupon),
              danger: true,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">Coupons</h1>
          <p className="text-secondary-500 dark:text-secondary-400">Create and manage discount coupons for your patients</p>
        </div>
        <Button leftIcon={<PlusIcon className="h-5 w-5" />} onClick={() => handleOpenModal()}>
          Create Coupon
        </Button>
      </div>

      {/* Table */}
      <Card padding="none">
        {coupons.length === 0 && !isLoading ? (
          <EmptyState
            icon={<TicketIcon className="h-12 w-12" />}
            title="No coupons yet"
            description="Create your first coupon to offer discounts to your patients"
            action={
              <Button
                leftIcon={<PlusIcon className="h-5 w-5" />}
                onClick={() => handleOpenModal()}
              >
                Create Coupon
              </Button>
            }
          />
        ) : (
          <Table
            columns={columns}
            data={coupons}
            keyExtractor={(coupon) => coupon.id}
            isLoading={isLoading}
            onRowClick={(coupon) => handleOpenModal(coupon)}
          />
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCoupon ? 'Edit Coupon' : 'Create Coupon'}
        description={editingCoupon ? 'Update the coupon details below' : 'Fill in the details to create a new coupon'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <svg className="h-5 w-5 text-warning-500 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
            <p className="text-sm text-warning-700 dark:text-warning-300">
              Coupons only apply to the first order/charge. Subsequent orders will be charged at the full price.
            </p>
          </div>

          <Input
            label="Coupon Code"
            name="code"
            value={formData.code}
            onChange={handleInputChange}
            placeholder="e.g., SUMMER20"
            required
            className="uppercase"
          />

          <Textarea
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Describe what this coupon is for..."
            required
            rows={2}
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Discount Type"
              name="discountType"
              value={formData.discountType}
              onChange={handleInputChange}
              options={discountTypeOptions}
              required
            />

            <Input
              label={formData.discountType === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
              name="discountValue"
              type="number"
              value={formData.discountValue}
              onChange={handleInputChange}
              placeholder={formData.discountType === 'percentage' ? '0-100' : '0.00'}
              min="0"
              max={formData.discountType === 'percentage' ? '100' : undefined}
              step={formData.discountType === 'percentage' ? '1' : '0.01'}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Expiration Date"
              name="expirationDate"
              type="date"
              value={formData.expirationDate}
              onChange={handleInputChange}
              required
            />

            <Input
              label="Usage Limit (optional)"
              name="usageLimit"
              type="number"
              value={formData.usageLimit}
              onChange={handleInputChange}
              placeholder="Unlimited"
              min="1"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleInputChange}
              className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="isActive" className="text-sm text-secondary-700 dark:text-secondary-300">
              Coupon is active
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-100 dark:border-secondary-700">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isSaving}>
              {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Coupon"
        message={`Are you sure you want to delete the coupon "${couponToDelete?.code}"? This action cannot be undone.`}
        confirmLabel="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Coupons;
